// src/pages/AuctionAnalysisPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AuctionAnalysisPage.css';
import TopBar from '../components/TopBar';
import TeamBadge from '../components/TeamBadge';
import Tooltip from '../components/Tooltip';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ensureFantasyDataSynced, getPlayersFromFirestore } from '../firebase/playerService';
import {
  getAuctionById,
  getAuctionPurchases,
  normalizeRosterLimits,
  DEFAULT_ROSTER_LIMITS,
  getAuctionRolesMode,
} from '../firebase/auctionService';
import {
  buildRoleOverrideMap,
  applyRoleOverrideToPurchase,
  applyLivePlayerDataToPurchase,
  buildTeamEconomy,
  buildRosterByTeam,
} from '../components/auction/auctionRosterUtils';
import {
  buildTeamRosterStats,
  rankTeams,
  isEarlySeasonSample,
  buildTeamInsightDetail,
} from '../components/auction/auctionAnalysis';
import { RoleChip, fmt, v, Metric, AuctionTeamAvatar } from '../components/auction/auctionCommon';
import {
  AnalysisKpiStrip,
  AnalysisAwardCards,
  PredictedFvChart,
  RoleSpendChart,
  AnalysisRoleTable,
  AnalysisRosterStrengths,
  AnalysisDraftReport,
  AnalysisRosterCalendar,
} from '../components/auction/AuctionAnalysisDashboard';
import {
  ArrowLeft,
  Trophy,
  Sparkles,
  Info,
  ExternalLink,
  X,
} from 'lucide-react';

function openPlayerDetailNewTab(playerId) {
  window.open(`${window.location.origin}/players/${playerId}`, '_blank', 'noopener,noreferrer');
}

function getPurchaseName(purchase) {
  return purchase?.playerSnapshot?.name || purchase?.playerName || '—';
}

function getPurchaseTeam(purchase) {
  return purchase?.playerSnapshot?.team || purchase?.playerTeam || '—';
}

const RACE_GRID_TIER = ['p1', 'p2', 'p3'];
const F1_TIER_COLORS = ['#facc15', '#cbd5e1', '#d97706'];
const F1_PALETTE = ['#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#14b8a6'];

function getF1CarColor(rank) {
  return F1_TIER_COLORS[rank] || F1_PALETTE[(rank - F1_TIER_COLORS.length) % F1_PALETTE.length];
}

// Monoposto in stile F1 moderna (era ground-effect) vista dall'alto, in verticale:
// ali larghe con endplate, gomme carenate, sidepod scavati, halo e pinna motore.
function F1CarIcon({ color, driverName, position, uid }) {
  const safeUid = String(uid).replace(/[^a-zA-Z0-9_-]/g, '-');
  const gradId = `f1carBody-${safeUid}`;
  const longName = String(driverName || '').length > 10;

  return (
    <svg viewBox="0 0 120 300" className="f1car__svg" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.65" />
          <stop offset="50%" stopColor={color} />
          <stop offset="100%" stopColor={color} stopOpacity="0.65" />
        </linearGradient>
      </defs>

      {/* ombra a terra */}
      <ellipse cx="60" cy="152" rx="50" ry="140" fill="rgba(0,0,0,0.22)" />

      {/* ala anteriore: due flap + endplate */}
      <rect x="5" y="6" width="7" height="24" rx="2.5" fill="#0d1117" />
      <rect x="108" y="6" width="7" height="24" rx="2.5" fill="#0d1117" />
      <path d="M12,9 C28,14 44,16 60,16 C76,16 92,14 108,9 L108,15 C92,20 76,22 60,22 C44,22 28,20 12,15 Z" fill="#161b22" />
      <path d="M12,18 C28,23 44,25 60,25 C76,25 92,23 108,18 L108,24 C92,29 76,31 60,31 C44,31 28,29 12,24 Z" fill="#1f242c" />

      {/* sospensioni anteriori */}
      <path d="M50,50 L30,46 M70,50 L90,46 M50,60 L30,64 M70,60 L90,64" stroke="#2a3038" strokeWidth="3" />

      {/* gomme anteriori */}
      <rect x="10" y="36" width="22" height="42" rx="8" fill="#0b0e13" />
      <rect x="14" y="41" width="14" height="32" rx="6" fill="#1c2129" />
      <rect x="88" y="36" width="22" height="42" rx="8" fill="#0b0e13" />
      <rect x="92" y="41" width="14" height="32" rx="6" fill="#1c2129" />

      {/* musetto */}
      <path d="M60,14 C55.5,14 54.5,19 54,27 L52,70 L68,70 L66,27 C65.5,19 64.5,14 60,14 Z" fill={`url(#${gradId})`} />

      {/* corpo: spalle, sidepod, coca-cola e coda */}
      <path
        d="M52,68 C44,72 38,78 34,88 C28,96 26,104 26,116 L26,148 C26,178 32,206 40,228 C44,240 50,248 60,248 C70,248 76,240 80,228 C88,206 94,178 94,148 L94,116 C94,104 92,96 86,88 C82,78 76,72 68,68 Z"
        fill={`url(#${gradId})`}
      />

      {/* bocche dei sidepod */}
      <path d="M28,100 L42,95 L42,112 L28,115 Z" fill="rgba(0,0,0,0.3)" />
      <path d="M92,100 L78,95 L78,112 L92,115 Z" fill="rgba(0,0,0,0.3)" />

      {/* numero sul musetto */}
      <circle cx="60" cy="44" r="11" fill="rgba(13,17,23,0.55)" stroke="rgba(255,255,255,0.65)" strokeWidth="1.5" />
      <text x="60" y="45" textAnchor="middle" dominantBaseline="middle" className="f1car__number">{position}</text>

      {/* halo + abitacolo + casco */}
      <line x1="60" y1="92" x2="60" y2="102" stroke="#0d1117" strokeWidth="4.5" />
      <ellipse cx="60" cy="110" rx="16" ry="12" fill="none" stroke="#0d1117" strokeWidth="4.5" />
      <ellipse cx="60" cy="116" rx="9" ry="12" fill="#0d1117" />
      <circle cx="60" cy="113" r="5.5" fill="#e5e7eb" />
      <rect x="55.5" y="110.5" width="9" height="3" rx="1.5" fill="#0d1117" />

      {/* nome pilota lungo il cofano motore */}
      <text
        x="60"
        y="176"
        textAnchor="middle"
        dominantBaseline="middle"
        className="f1car__driver"
        transform="rotate(90 60 176)"
        {...(longName ? { textLength: 86, lengthAdjust: 'spacingAndGlyphs' } : {})}
      >
        {driverName}
      </text>

      {/* pinna motore verso la coda */}
      <rect x="57.5" y="214" width="5" height="34" rx="2.5" fill="rgba(0,0,0,0.25)" />

      {/* sospensioni posteriori */}
      <path d="M50,222 L28,220 M70,222 L92,220 M50,236 L28,240 M70,236 L92,240" stroke="#2a3038" strokeWidth="3" />

      {/* gomme posteriori (più larghe) */}
      <rect x="6" y="204" width="26" height="50" rx="9" fill="#0b0e13" />
      <rect x="11" y="210" width="16" height="38" rx="6" fill="#1c2129" />
      <rect x="88" y="204" width="26" height="50" rx="9" fill="#0b0e13" />
      <rect x="93" y="210" width="16" height="38" rx="6" fill="#1c2129" />

      {/* ala posteriore: flap DRS, piano principale e beam wing */}
      <rect x="40" y="250" width="40" height="5" rx="2" fill="#1c2129" />
      <rect x="15" y="256" width="7" height="34" rx="2.5" fill="#0d1117" />
      <rect x="98" y="256" width="7" height="34" rx="2.5" fill="#0d1117" />
      <rect x="20" y="260" width="80" height="9" rx="3" fill="#161b22" />
      <rect x="20" y="273" width="80" height="11" rx="4" fill="#0d1117" />
      <rect x="20" y="273" width="80" height="3" rx="1.5" fill={color} opacity="0.85" />
    </svg>
  );
}

function RaceGridCard({ stat, rank, isMine, onOpenDetail }) {
  const tier = RACE_GRID_TIER[rank] || 'other';
  const color = getF1CarColor(rank);
  const driverName = stat.topSpendPlayer ? getPurchaseName(stat.topSpendPlayer) : stat.team;

  return (
    <div
      className={`race-grid__card race-grid__card--${tier}${isMine ? ' race-grid__card--mine' : ''}`}
      onClick={() => onOpenDetail(stat)}
      role="button"
      tabIndex={0}
      aria-label={`P${rank + 1} ${stat.team}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenDetail(stat); }}
    >
      <span className="race-grid__posWatermark" aria-hidden="true">P{rank + 1}</span>
      <F1CarIcon color={color} driverName={driverName} position={rank + 1} uid={`${rank}-${stat.team}`} />
      <div className="race-grid__info">
        <Tooltip as="span" className="race-grid__teamName" content={stat.team}>{stat.team}</Tooltip>
        <span className="race-grid__score"><strong>{fmt(stat.powerScore)}</strong> pt</span>
        {isMine && <span className="race-grid__mineTag">La tua squadra</span>}
      </div>
    </div>
  );
}

function RaceGrid({ rankedTeams, myTeamName, onOpenDetail }) {
  return (
    <div className="race-grid-track">
      <div className="race-grid-track__header">
        <span className="race-grid-track__label">Griglia di partenza</span>
        <div className="race-grid-track__checker" />
      </div>

      <div className="race-grid">
        {rankedTeams.map((stat, idx) => (
          <RaceGridCard key={stat.team} stat={stat} rank={idx} isMine={stat.team === myTeamName} onOpenDetail={onOpenDetail} />
        ))}
      </div>
    </div>
  );
}

function TeamDetailOverlay({ stat, onClose }) {
  const detail = useMemo(() => buildTeamInsightDetail(stat), [stat]);
  if (!detail) return null;

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-panel glass" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <AuctionTeamAvatar name={stat.team} />
            <div>
              <span>Dettaglio squadra</span>
              <h3>{stat.team}</h3>
            </div>
          </div>
          <button className="overlay-close" type="button" onClick={onClose} aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>

        <div className="team-detail-grid">
          <div className="team-detail-stat">
            <span>Media voto prevista</span>
            <strong>{fmt(detail.predictedMv)}</strong>
          </div>
          <div className="team-detail-stat">
            <span>Fantamedia prevista</span>
            <strong>{fmt(detail.predictedFv)}</strong>
          </div>
          <div className="team-detail-stat team-detail-stat--accent">
            <span>Punteggio totale (classifica)</span>
            <strong>{fmt(detail.powerScore)}</strong>
          </div>
        </div>

        <div className="team-detail-players">
          <div className="team-detail-player-card">
            <span className="team-detail-player-label">💰 Giocatore quota più alta</span>
            {detail.topSpendPlayer ? (
              <div className="team-detail-player-row">
                <TeamBadge team={getPurchaseTeam(detail.topSpendPlayer)} size={30} />
                <div className="team-detail-player-row__body">
                  <strong>{getPurchaseName(detail.topSpendPlayer)}</strong>
                  <span>{detail.topSpendPlayer.credits} cr</span>
                </div>
                <Tooltip
                  as="button"
                  type="button"
                  className="team-detail-player-btn"
                  onClick={() => openPlayerDetailNewTab(detail.topSpendPlayer.playerId)}
                  content="Apri dettaglio giocatore"
                >
                  <ExternalLink size={13} />
                </Tooltip>
              </div>
            ) : (
              <span className="mm-muted">Nessun giocatore</span>
            )}
          </div>

          <div className="team-detail-player-card">
            <span className="team-detail-player-label">🎲 Giocatore scommessa</span>
            {detail.bargainPlayer ? (
              <div className="team-detail-player-row">
                <TeamBadge team={getPurchaseTeam(detail.bargainPlayer)} size={30} />
                <div className="team-detail-player-row__body">
                  <strong>{getPurchaseName(detail.bargainPlayer)}</strong>
                  <span>
                    {detail.bargainPlayer.credits} cr · {v(detail.bargainPlayer.playerSnapshot?.goals)}G {v(detail.bargainPlayer.playerSnapshot?.assists)}A
                  </span>
                </div>
                <Tooltip
                  as="button"
                  type="button"
                  className="team-detail-player-btn"
                  onClick={() => openPlayerDetailNewTab(detail.bargainPlayer.playerId)}
                  content="Apri dettaglio giocatore"
                >
                  <ExternalLink size={13} />
                </Tooltip>
              </div>
            ) : (
              <span className="mm-muted">Nessun giocatore pagato 1 credito.</span>
            )}
          </div>
        </div>

        <div className="team-detail-roles">
          <strong>Fantamedia prevista per ruolo</strong>
          <div className="team-detail-roles__grid">
            {detail.predictedFvByRole.map((r) => (
              <div key={r.role} className="team-detail-role-item">
                <RoleChip role={r.role} />
                <span>{r.owned ? fmt(r.predictedFv) : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function AuctionAnalysisPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { auctionId } = useParams();
  const [searchParams] = useSearchParams();
  const ownerUid = searchParams.get('ownerUid') || user?.uid;

  const [loading, setLoading] = useState(true);
  const [auction, setAuction] = useState(null);
  const [players, setPlayers] = useState([]);
  const [purchases, setPurchases] = useState([]);

  const rolesMode = getAuctionRolesMode(auction);
  const roleOverrideMap = useMemo(() => buildRoleOverrideMap(auction), [auction]);

  const rosterLimits = useMemo(
    () => normalizeRosterLimits(auction?.rosterLimits || DEFAULT_ROSTER_LIMITS),
    [auction]
  );

  const teamNames = useMemo(() => auction?.teamNames || [], [auction]);
  const myTeamName = auction?.myTeamName || teamNames[auction?.myTeamIndex || 0] || teamNames[0] || '';
  const isSoloAuction = teamNames.length <= 1;

  const playersById = useMemo(() => {
    const map = {};
    players.forEach((player) => { map[String(player.id)] = player; });
    return map;
  }, [players]);

  const effectivePurchases = useMemo(
    () => purchases
      .map((purchase) => applyRoleOverrideToPurchase(purchase, roleOverrideMap))
      .map((purchase) => applyLivePlayerDataToPurchase(purchase, playersById)),
    [purchases, roleOverrideMap, playersById]
  );

  const teamEconomy = useMemo(
    () => buildTeamEconomy({ teamNames, purchases: effectivePurchases, auction, rosterLimits, rolesMode }),
    [teamNames, effectivePurchases, auction, rosterLimits, rolesMode]
  );

  const rosterByTeam = useMemo(
    () => buildRosterByTeam({ teamNames, purchases: effectivePurchases }),
    [teamNames, effectivePurchases]
  );

  const teamStats = useMemo(
    () => buildTeamRosterStats({ teamNames, rosterByTeam, teamEconomy, rolesMode, auction }),
    [teamNames, rosterByTeam, teamEconomy, rolesMode, auction]
  );

  const rankedTeams = useMemo(() => rankTeams(teamStats), [teamStats]);

  const earlySeasonNotice = useMemo(() => isEarlySeasonSample(teamStats), [teamStats]);

  const [activeTeamDetail, setActiveTeamDetail] = useState(null);

  const loadAll = useCallback(async () => {
    if (!user?.uid || !auctionId) return;

    setLoading(true);
    try {
      const auctionData = await getAuctionById(ownerUid, auctionId);
      if (!auctionData) {
        setAuction(null);
        setPlayers([]);
        setPurchases([]);
        return;
      }

      let currentPlayers = await getPlayersFromFirestore().catch(() => []);
      if (!currentPlayers.length) {
        await ensureFantasyDataSynced();
        currentPlayers = await getPlayersFromFirestore().catch(() => []);
      }

      const purchaseData = await getAuctionPurchases(ownerUid, auctionId);

      setAuction({
        ...auctionData,
        id: auctionId,
        rosterLimits: normalizeRosterLimits(auctionData.rosterLimits || DEFAULT_ROSTER_LIMITS),
      });
      setPlayers(currentPlayers);
      setPurchases(purchaseData);
    } finally {
      setLoading(false);
    }
  }, [auctionId, user?.uid, ownerUid]);

  useEffect(() => {
    loadAll().catch(() => setLoading(false));
  }, [loadAll]);


  if (loading || !auction) {
    return (
      <main className="container auction-analysis-page">
        <TopBar />
        <div className="auction-detail-empty glass">
          {loading ? 'Caricamento analisi...' : 'Asta non trovata.'}
        </div>
      </main>
    );
  }

  return (
    <main className="container auction-analysis-page">
      <TopBar />

      <section className="auction-detail-hero glass">
        <div>
          <button className="back-btn" onClick={() => navigate(`/aste/${auctionId}${ownerUid !== user?.uid ? `?ownerUid=${ownerUid}` : ''}`)} type="button">
            <ArrowLeft size={16} />
            Torna all'asta
          </button>

          <div className="auction-detail-hero__eyebrow">
            <Sparkles size={14} />
            Analisi asta · {rolesMode === 'mantra' ? 'Mantra' : 'Classic'}
          </div>

          <h1>{auction.name}</h1>
        </div>

        <div className="auction-detail-hero__summary">
          <Metric label="Squadre" value={teamNames.length} />
          <Metric label="Crediti" value={auction.credits} />
          <Metric label="La tua squadra" value={myTeamName || '—'} />
        </div>
      </section>

      <section className="analysis-dashboard">
        <div className="analysis-dashboard__intro glass">
          <Trophy size={18} />
          <div>
            {isSoloAuction ? (
              <>
                <strong>La tua rosa</strong>
                <span>Sei l'unico partecipante di questa asta: qui trovi i punti di forza/debolezza della tua rosa e il calendario dei tuoi giocatori. Le statistiche di confronto tra squadre compariranno quando ci sarà almeno un altro partecipante.</span>
              </>
            ) : (
              <>
                <strong>Chi ha fatto la miglior asta</strong>
                <span>Griglia di partenza, statistiche per reparto e grafici di spesa: classifica basata sull'indice di rosa (statistiche stagione corrente e precedente, presenze giocate, realizzazione offensiva, completamento della formazione).</span>
              </>
            )}
          </div>
        </div>

        {!isSoloAuction && <AnalysisKpiStrip teamStats={teamStats} rankedTeams={rankedTeams} />}

        {earlySeasonNotice && (
          <div className="analysis-dashboard__note">
            <Info size={14} />
            Campionato appena iniziato: le presenze in stagione corrente sono ancora poche, quindi il ranking integra anche le statistiche dell'anno precedente per essere più affidabile.
          </div>
        )}

        <div className={`analysis-bento${isSoloAuction ? ' analysis-bento--solo' : ''}`}>
          {!isSoloAuction && (
            <>
              <div className="analysis-bento__panel analysis-bento__panel--grid">
                <RaceGrid rankedTeams={rankedTeams} myTeamName={myTeamName} onOpenDetail={setActiveTeamDetail} />
              </div>

              <div className="analysis-bento__panel analysis-bento__panel--award">
                <AnalysisAwardCards teamStats={teamStats} onOpenTeamDetail={setActiveTeamDetail} />
              </div>

              <div className="analysis-bento__panel analysis-bento__panel--chart1">
                <PredictedFvChart teamStats={teamStats} myTeamName={myTeamName} />
              </div>

              <div className="analysis-bento__panel analysis-bento__panel--chart2">
                <RoleSpendChart teamStats={teamStats} myTeamName={myTeamName} />
              </div>

              <div className="analysis-bento__panel analysis-bento__panel--table">
                <AnalysisRoleTable teamStats={teamStats} onOpenTeamDetail={setActiveTeamDetail} />
              </div>
            </>
          )}

          <div className="analysis-bento__panel analysis-bento__panel--strengths">
            <AnalysisRosterStrengths teamStats={teamStats} myTeamName={myTeamName} />
          </div>

          <div className="analysis-bento__panel analysis-bento__panel--draft">
            <AnalysisDraftReport teamStats={teamStats} myTeamName={myTeamName} />
          </div>

          <div className="analysis-bento__panel analysis-bento__panel--roster-cal">
            <AnalysisRosterCalendar teamStats={teamStats} myTeamName={myTeamName} />
          </div>
        </div>
      </section>

      {activeTeamDetail && (
        <TeamDetailOverlay stat={activeTeamDetail} onClose={() => setActiveTeamDetail(null)} />
      )}

      <footer className="auctions-footer">
        <span>FantApp! - All rights reserved - Sorgenti giocatori </span>
        <a href="https://www.fantamaster.it/" target="_blank" rel="noreferrer">
          FantaMaster
        </a>
      </footer>

    </main>
  );
}
